/**
 * Een lap gesproken tekst in leesregels knippen.
 *
 * Het model levert het transcript en de vertaling als EEN string terug, zonder
 * regelovergangen. Op het scherm werd dat een blok van vier, vijf zinnen achter
 * elkaar - je moet het twee keer lezen om te zien waar de ene zin ophoudt en de
 * volgende begint. Dat is precies wat je niet wilt in een gesprek dat je live
 * volgt.
 *
 * Daarom knippen we hier, in de browser, op zinsgrens. Dat kost niets extra en
 * het gaat altijd hetzelfde: geen tweede aanroep naar het model, geen wisselend
 * resultaat.
 *
 * WAT WE NIET DOEN: de tekst zelf veranderen. Er komt geen woord bij en er gaat
 * er geen af. Alleen de plek van de regelovergangen verandert.
 */

/** Interpunctie die in het latijnse schrift een zin afsluit. */
const EINDE_LATIJN = new Set(['.', '!', '?', '…']);

/**
 * Interpunctie die in het Chinees, Japans en Arabisch een zin afsluit.
 *
 * Deze tekens staan nooit midden in een woord of getal, dus hier mag altijd
 * geknipt worden - ook zonder spatie erachter, want die talen zetten er vaak
 * geen.
 */
const EINDE_BREED = new Set(['。', '！', '？', '．', '؟', '۔']);

/** Kleine letter in welk schrift dan ook. */
const KLEINE_LETTER = /\p{Ll}/u;

/**
 * Aanhef aan het begin: "Hoi Joy," of "Goedemorgen mevrouw Jansen,".
 *
 * Hoogstens drie woorden voor de komma. Langer is geen aanhef meer maar een
 * bijzin, en die hoort gewoon op dezelfde regel te blijven staan.
 */
const AANHEF = /^(\S+(?:\s+\S+){0,2},)\s+(?=\S)/u;

/**
 * Knip een stuk tekst op zinsgrenzen.
 *
 * Bij een punt kijken we naar wat erna komt. Zit er geen spatie achter, dan
 * stond de punt ergens IN - "1.500 euro", "www.nos.nl". Volgt er een kleine
 * letter, dan was het een afkorting: "bijv. morgen". In beide gevallen blijft
 * de zin heel.
 */
function knipZinnen(stuk: string): string[] {
  const zinnen: string[] = [];
  const tekens = [...stuk];
  let huidig = '';

  for (let i = 0; i < tekens.length; i++) {
    const teken = tekens[i] as string;
    huidig += teken;

    if (EINDE_BREED.has(teken)) {
      zinnen.push(huidig.trim());
      huidig = '';
      continue;
    }

    if (!EINDE_LATIJN.has(teken)) continue;

    // Meerdere leestekens achter elkaar ("...", "?!") horen bij elkaar.
    if (EINDE_LATIJN.has(tekens[i + 1] ?? '')) continue;

    // Een sluitend aanhalingsteken of haakje hoort nog bij deze zin.
    let na = i + 1;
    while (['"', "'", '”', '’', ')', '»', '›'].includes(tekens[na] ?? '')) {
      huidig += tekens[na] as string;
      na++;
    }
    i = na - 1;

    const volgend = tekens[na];
    if (volgend === undefined) break;
    // Geen spatie erachter: dan zat de punt IN iets - "1.500", "www.nos.nl".
    if (!/\s/u.test(volgend)) continue;

    const rest = tekens.slice(na).join('').trimStart();
    if (rest === '') break;
    // Kleine letter erna: een afkorting, zoals "bijv. morgen".
    if (KLEINE_LETTER.test(rest[0] as string)) continue;

    zinnen.push(huidig.trim());
    huidig = '';
  }

  if (huidig.trim() !== '') zinnen.push(huidig.trim());
  return zinnen;
}

/**
 * De hele tekst als losse leesregels.
 *
 * Regelovergangen die er al in zaten, blijven staan. Lege regels vallen weg.
 *
 * `aanhefApart: false` is voor het gespreksverslag. In een gesprek dat je live
 * volgt, helpt een eigen regel voor "Hoi Joy,"; op papier wordt "Ja, / graag."
 * twee regels voor twee woorden. Standaard blijft het zoals het gesprek het
 * toont.
 */
export function alsRegels(tekst: string, opties: { aanhefApart?: boolean } = {}): string[] {
  const regels: string[] = [];

  for (const stuk of tekst.split('\n')) {
    const schoon = stuk.trim();
    if (schoon === '') continue;

    // De aanhef krijgt een eigen regel: "Hoi Joy," leest anders door in de zin
    // erna, en juist daar begint het verhaal.
    const aanhef = opties.aanhefApart === false ? null : AANHEF.exec(schoon);
    if (aanhef !== null) {
      regels.push(aanhef[1] as string);
      regels.push(...knipZinnen(schoon.slice(aanhef[0].length)));
      continue;
    }

    regels.push(...knipZinnen(schoon));
  }

  return regels.length > 0 ? regels : [tekst.trim()];
}

/** Dezelfde regels, maar als een string met echte regelovergangen erin. */
export function metRegelovergangen(tekst: string): string {
  return alsRegels(tekst).join('\n');
}
